/* eslint no-console: 0 */
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { Device, useGetCurrentUserDevices } from '../../../hooks/useGetCurrentUserDevices.ts';
import { DeviceStatusIndicator } from '../../bits/DeviceStatusIndicator.tsx';
import Loading from '../Loading.jsx';

interface DevicesListProps { }

const DeviceGrouping = styled.div`
  margin-bottom: 36px;
  h1 {
    font-size: 1.6em;
    font-weight: 500;
    margin-bottom: 0px;
  }
`;

const DisclaimerText = styled.div`
  font-size: 0.8em;
  margin-bottom: 16px;
  line-height: 1.4
`;

const DeviceLink: React.FC<{ device: Device }> = ({ device }) => {
  const { id, name } = device;

  return (
    <Link
      to={{
        pathname: `/m/devices/${id}`,
        state: { origin: 'devices_list' },
      }}
      className="btnlist__btn">
      <span className="btnlist__label">
        <DeviceStatusIndicator device={device} />
        {name}
      </span>
      <span className="btnlist__icon">
        <i className="fa fa-chevron-right"></i>
      </span>
    </Link>
  );
};


const DevicesGroupedList = ({ devices }: { devices: Device[] }) => {
  const activeDevices = devices.filter(d => d.state === 'active').sort((a, b) => a.name.localeCompare(b.name));
  const suspendedDevices = devices.filter(d => d.state !== 'active').sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <DeviceGrouping>
        <h1>Active</h1>
        { activeDevices.length > 0 ? (
          <div className="btnlist" style={{ marginTop: '8px' }}>
            {activeDevices.map(device => <DeviceLink key={device.id} device={device} />)}
          </div>
        ) : (
          <div>You have no active devices.</div>
        )}
      </DeviceGrouping>

      <DeviceGrouping>
        <h1>Suspended</h1>
        { suspendedDevices.length > 0 ? (
          <>
            <DisclaimerText>You can reactivate any of your suspended devices by selecting a new plan under the 'Service Plan' section for each device. If you need help, contact us via our 
              <Link
                to={{
                  pathname: `/m/help/contact-us`,
                  state: { origin: 'devices_list' },
                }}> Help Form.</Link>
              </DisclaimerText>

            <div className="btnlist">
              {suspendedDevices.map(device => <DeviceLink key={device.id} device={device} />)}
            </div>
          </>
        ) : (
          <div>You have no suspended devices.</div>
        )}
      </DeviceGrouping>
    </>
  );
}
/**
 * Component that lists devices belonging to the user.
 */
export const DevicesList: React.FC<DevicesListProps> = () => {
  const { data: devices, isFetching } = useGetCurrentUserDevices();

  useSetModalTitle('Devices');

  // Show loading indicator if we're fetching content AND have nothing to display. This means
  // refreshes will not show loading. Important because of the query invalidation that can happen
  // (we want query invalidation to show loading)
  const isLoading = isFetching && !devices;

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && devices && <DevicesGroupedList devices={devices} />}
    </>
  );
};
